import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { dataProvider } from '@/lib/dataProvider';

const ClassCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  
  const [formData, setFormData] = useState<any>({
    title: '',
    description: '',
    courseId: searchParams.get('courseId') || '',
    teacherId: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(new Date().setMonth(new Date().getMonth() + 3)).toISOString().split('T')[0],
    enrollmentLimit: undefined,
    status: 'upcoming',
  });

  const [courses, setCourses] = useState<any[]>([]);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Get data from dataProvider
        setTimeout(() => {
          const coursesData: any[] = dataProvider.getCreateCourses();
          const teachersData: any[] = dataProvider.getTeachers();

          setCourses(coursesData);
          setTeachers(teachersData);
          
          // If courseId is provided in URL params, auto-select it and set title
          if (searchParams.get('courseId')) {
            const selectedCourse = coursesData.find((c: any) => c.id === searchParams.get('courseId'));
            if (selectedCourse) {
              setFormData((prev: any) => ({
                ...prev,
                courseId: selectedCourse.id,
                title: selectedCourse.title,
              }));
            }
          }
          
          setIsLoading(false);
        }, 1000);
      } catch (err) {
        setError('Failed to load form data');
        setIsLoading(false);
      }
    };

    loadData();
  }, [searchParams]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Handle nested fields
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData((prev: any) => ({
        ...prev,
        [parent]: {
          ...((prev as any)[parent] || {}),
          [child]: value,
        },
      }));
    } else {
      setFormData((prev: any) => ({
        ...prev,
        [name]: name === 'enrollmentLimit' ? (value ? parseInt(value, 10) : undefined) : value,
      }));
    }
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors((prev: any) => ({ ...prev, [name]: '' }));
    }
  };

  const handleCourseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const courseId = e.target.value;
    const selectedCourse = courses.find((c: any) => c.id === courseId);
    
    setFormData((prev: any) => ({
      ...prev,
      courseId,
      title: selectedCourse ? selectedCourse.title : prev.title,
    }));
    
    if (formErrors.courseId) {
      setFormErrors((prev: any) => ({ ...prev, courseId: '' }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.title?.trim()) {
      errors.title = 'Tên lớp học là bắt buộc';
    }
    
    if (!formData.courseId) {
      errors.courseId = 'Vui lòng chọn khóa học';
    }
    
    if (!formData.teacherId) {
      errors.teacherId = 'Vui lòng chọn giảng viên';
    }
    
    if (!formData.startDate) {
      errors.startDate = 'Ngày bắt đầu là bắt buộc';
    }
    
    if (!formData.endDate) {
      errors.endDate = 'Ngày kết thúc là bắt buộc';
    }
    
    if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
      errors.endDate = 'Ngày kết thúc phải sau ngày bắt đầu';
    }
    
    if (formData.enrollmentLimit && formData.enrollmentLimit < 1) {
      errors.enrollmentLimit = 'Giới hạn đăng ký phải lớn hơn 0';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Simulate API call
      setTimeout(() => {
        console.log('Creating class with data:', formData);
        
        // Navigate to the class detail page (simulated ID)
        const newClassId = `class-${Date.now()}`;
        navigate(`/lms/classes/${newClassId}`);
      }, 2000);
    } catch (err) {
      setError('Failed to create class. Please try again.');
      setIsSubmitting(false);
    }
  };

  const selectedCourse = courses.find((c: any) => c.id === formData.courseId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Đang tải...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="px-4 sm:px-6 lg:max-w-6xl lg:mx-auto lg:px-8">
          <div className="py-6 md:flex md:items-center md:justify-between lg:border-t lg:border-gray-200">
            <div className="flex-1 min-w-0">
              <div className="flex items-center">
                <Link 
                  to="/lms/classes"
                  className="mr-4 text-gray-500 hover:text-gray-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
                <div>
                  <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:leading-9 sm:truncate">
                    Tạo lớp học mới
                  </h1>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Basic Information */}
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4">Thông tin cơ bản</h2>
                
                {/* Course Selection */}
                <div className="grid grid-cols-1 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="courseId">
                      Khóa học <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="courseId"
                      name="courseId"
                      value={formData.courseId}
                      onChange={handleCourseChange}
                      className={`block w-full rounded-md border ${
                        formErrors.courseId ? 'border-red-500' : 'border-gray-300'
                      } px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    >
                      <option value="">Chọn khóa học</option>
                      {courses.map((course: any) => (
                        <option key={course.id} value={course.id}>
                          {course.title} - {course.subject} (Cấp {course.level})
                        </option>
                      ))}
                    </select>
                    {formErrors.courseId && <p className="mt-1 text-sm text-red-600">{formErrors.courseId}</p>}
                  </div>
                </div>

                {/* Course Info Display */}
                {selectedCourse && (
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                    <h3 className="text-sm font-medium text-blue-900 mb-2">Thông tin khóa học đã chọn:</h3>
                    <p className="text-sm text-blue-800 mb-1"><strong>Mô tả:</strong> {selectedCourse.description}</p>
                    <p className="text-sm text-blue-800 mb-1"><strong>Thời lượng:</strong> {selectedCourse.duration.value} {selectedCourse.duration.unit === 'week' ? 'tuần' : selectedCourse.duration.unit === 'month' ? 'tháng' : 'ngày'}</p>
                    <p className="text-sm text-blue-800"><strong>Hình thức:</strong> {selectedCourse.mode === 'blended' ? 'Kết hợp' : selectedCourse.mode === 'live' ? 'Trực tiếp' : 'Tự học'}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Class Title */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="title">
                      Tên lớp học <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Nhập tên lớp học"
                      className={`block w-full rounded-md border ${
                        formErrors.title ? 'border-red-500' : 'border-gray-300'
                      } px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    />
                    {formErrors.title && <p className="mt-1 text-sm text-red-600">{formErrors.title}</p>}
                  </div>

                  {/* Class Description */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="description">
                      Mô tả lớp học
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Nhập mô tả chi tiết về lớp học"
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  {/* Teacher Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="teacherId">
                      Giảng viên <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="teacherId"
                      name="teacherId"
                      value={formData.teacherId}
                      onChange={handleInputChange}
                      className={`block w-full rounded-md border ${
                        formErrors.teacherId ? 'border-red-500' : 'border-gray-300'
                      } px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    >
                      <option value="">Chọn giảng viên</option>
                      {teachers.map((teacher: any) => (
                        <option key={teacher.id} value={teacher.id}>
                          {teacher.name} ({teacher.role}) - {teacher.department}
                        </option>
                      ))}
                    </select>
                    {formErrors.teacherId && <p className="mt-1 text-sm text-red-600">{formErrors.teacherId}</p>}
                  </div>

                  {/* Enrollment Limit */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="enrollmentLimit">
                      Giới hạn đăng ký
                    </label>
                    <input
                      type="number"
                      id="enrollmentLimit"
                      name="enrollmentLimit"
                      value={formData.enrollmentLimit || ''}
                      onChange={handleInputChange}
                      min="1"
                      placeholder="Không giới hạn"
                      className={`block w-full rounded-md border ${
                        formErrors.enrollmentLimit ? 'border-red-500' : 'border-gray-300'
                      } px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    />
                    {formErrors.enrollmentLimit && <p className="mt-1 text-sm text-red-600">{formErrors.enrollmentLimit}</p>}
                    <p className="mt-1 text-sm text-gray-500">Để trống nếu không muốn giới hạn số lượng học sinh</p>
                  </div>
                </div>
              </div>

              {/* Schedule */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4">Lịch học</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="startDate">
                      Ngày bắt đầu <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      className={`block w-full rounded-md border ${
                        formErrors.startDate ? 'border-red-500' : 'border-gray-300'
                      } px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    />
                    {formErrors.startDate && <p className="mt-1 text-sm text-red-600">{formErrors.startDate}</p>}
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="endDate">
                      Ngày kết thúc <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      className={`block w-full rounded-md border ${
                        formErrors.endDate ? 'border-red-500' : 'border-gray-300'
                      } px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    />
                    {formErrors.endDate && <p className="mt-1 text-sm text-red-600">{formErrors.endDate}</p>}
                  </div>
                </div>
              </div>

              {/* Status */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4">Trạng thái</h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="status">
                    Trạng thái lớp học
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="upcoming">Sắp diễn ra</option>
                    <option value="active">Đang diễn ra</option>
                    <option value="completed">Đã hoàn thành</option>
                    <option value="cancelled">Đã hủy</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <Link
                to="/lms/classes"
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Hủy
              </Link>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSubmitting && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isSubmitting ? 'Đang tạo...' : 'Tạo lớp học'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClassCreatePage; 